import { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../../hooks/useAppState';
import { useNotification } from '../../context/NotificationContext';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import './FlyerTemplates.css';

function formatDate(strDate) {
  const months = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
  ];

  const date = new Date(strDate);
  const day = date.getDate();
  const month = months[date.getMonth()];
  const year = date.getFullYear().toString().substr(-2);

  return `${day} ${month} ${year}`;
}

// Icons as inline SVGs
const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const EditIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const TrashIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const CalendarIcon = () => (
  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const ImagePlaceholder = () => (
  <svg className="placeholder-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

function FlyerTemplates() {
  const { getAccessTokenSilently } = useAuth0();
  const { organizationId } = useAppState();
  const [flyerTemplates, setFlyerTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const notification = useNotification();

  const navigate = useNavigate();

  const handleCreateNew = () => {
    navigate('/flyertemplatemanager');
  };

  const handleEditClick = (flyerTemplate) => {
    navigate('/flyertemplatemanager', { state: { flyerTemplate: flyerTemplate } });
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    const flyerTemplateId = templateToDelete._id || templateToDelete.id;
    setDeleteDialogOpen(false);

    try {
      const token = await getAccessTokenSilently();
      const response = await axios.delete(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyertemplates/${flyerTemplateId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.status === 200) {
        notification.success('Template was successfully deleted.');
        setFlyerTemplates(prevFlyerTemplates => prevFlyerTemplates.filter(flyerTemplate => (flyerTemplate._id || flyerTemplate.id) !== flyerTemplateId));
      }
    } catch (error) {
      notification.error('Error occurred while deleting the template.');
    }
    setTemplateToDelete(null);
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTemplateToDelete(null);
  };

  useEffect(() => {
    const fetchFlyerTemplates = async () => {
      if (!organizationId) return;

      setIsLoading(true);
      try {
        const token = await getAccessTokenSilently();
        const response = await axios.get(`${process.env.REACT_APP_SERVER_DOMAIN}/api/flyertemplates/organization/${organizationId}`, {
          headers: {
            Authorization: `Bearer ${token}`
          }
        });
        setFlyerTemplates(response.data);
      } catch (error) {
        console.error('Failed to fetch templates:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchFlyerTemplates();
  }, [getAccessTokenSilently, organizationId]);

  return (
    <div className="flyer-templates-page">

      {/* Delete Confirmation Dialog */}
      <ConfirmDialog
        open={deleteDialogOpen}
        title="Delete this template?"
        description="This action cannot be undone."
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        confirmText="Delete"
        cancelText="Cancel"
      />

      {/* Header */}
      <div className="templates-header">
        <div className="templates-header-content">
          <div className="templates-header-left">
            <h1 className="templates-title">Flyer Templates</h1>
            <p className="templates-count">
              {flyerTemplates.length} {flyerTemplates.length === 1 ? 'template' : 'templates'}
            </p>
          </div>

          <button className="create-template-btn" onClick={handleCreateNew}>
            <PlusIcon />
            <span>Create New Template</span>
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="templates-content">
        {isLoading ? (
          <div className="templates-loading">
            <div className="loading-spinner"></div>
            <p>Loading templates...</p>
          </div>
        ) : flyerTemplates.length === 0 ? (
          <div className="templates-empty">
            <div className="empty-icon-wrapper">
              <PlusIcon />
            </div>
            <h3>No templates yet</h3>
            <p>Create your first flyer template to get started</p>
            <button className="create-template-btn secondary" onClick={handleCreateNew}>
              <PlusIcon />
              <span>Create New Template</span>
            </button>
          </div>
        ) : (
          <div className="templates-grid">
            {flyerTemplates.map((template) => (
              <div key={template._id || template.id} className="template-card">
                {/* Thumbnail */}
                <div className="template-thumbnail flyer-thumbnail">
                  {template.preview_image_url ? (
                    <img
                      src={template.preview_image_url}
                      alt="Flyer Template"
                      className="template-image"
                    />
                  ) : (
                    <div className="template-placeholder">
                      <ImagePlaceholder />
                    </div>
                  )}

                  {/* Hover Actions */}
                  <div className="template-actions-overlay">
                    <button
                      className="template-action-btn edit"
                      onClick={() => handleEditClick(template)}
                    >
                      <EditIcon />
                      <span>Edit</span>
                    </button>
                    <button
                      className="template-action-btn delete"
                      onClick={() => handleDeleteClick(template)}
                    >
                      <TrashIcon />
                    </button>
                  </div>
                </div>

                {/* Info */}
                <div className="template-info">
                  <span className="template-date">
                    <CalendarIcon />
                    {formatDate(template.updated_at)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default FlyerTemplates;
